/**
 * Button Component
 */

import * as classNames from 'classnames';
import { LocationDescriptor } from 'found';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import './styles.scss';

import { getColorSchemeClassName } from '../../../styles/color-schemes';
import { getBackgroundColorClassName, getTextColorClassName } from '../../../styles/colors';
import { Span } from '../../text/Label';

interface IOwnProps extends ITextColorProps, IBackgroundColorProps, React.HTMLAttributes<HTMLButtonElement> {
  className?: string;
  colorScheme?: ColorScheme;
  dangerous?: boolean;
  disabled?: boolean;
  hyperLinkProps?: {
    href?: string;
    target?: string;
  };
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  isSubmit?: boolean;
  label?: React.ReactNode | string;
  onClick?: (data?: any) => any | void;
  positive?: boolean;
  routerLinkProps?: {
    to: LocationDescriptor;
    activeClassName?: string;
  };
  size?: ButtonSize;
  style?: React.CSSProperties;
  submitting?: boolean;
  title?: string;
  variant?: ButtonVariant;
  withShadow?: boolean;
  wrapperComponent?: ButtonWrapperType;
}

export type ButtonProps = IOwnProps;

const classes = new BEM('button');

export default class Button extends React.Component<IOwnProps, {}> {
  static defaultProps: Partial<IOwnProps> = {
    iconPosition: 'left',
    size: 'medium',
    variant: 'normal',
    wrapperComponent: 'button',
  };

  render() {
    const {
      backgroundColor,
      children,
      className,
      colorScheme,
      dangerous,
      disabled,
      hyperLinkProps,
      icon,
      iconPosition,
      isSubmit,
      label,
      positive,
      routerLinkProps,
      size,
      style,
      submitting,
      textColor,
      title,
      variant,
      withShadow,
      wrapperComponent,
      ...restProps
    } = this.props;

    const WrapperComponent: ButtonWrapperType = wrapperComponent!;
    const wrapperProps: any = {
      disabled,
      style,
      ...restProps,
    };

    const Icon = icon ?
      React.cloneElement((icon as React.ReactElement<any>), {
        className: classNames(
          (icon as React.ReactElement<any>).props.className || '',
          'button__icon',
        ),
      }) : null;

    if (WrapperComponent === 'button') {
      wrapperProps.type = isSubmit ? 'submit' : 'button';
    }

    const classesModifiers = {
      [size!]: true,
      [variant!]: variant !== 'normal',
      submitting: !!submitting,
    };

    // Implement text/background color classes
    const extraClasses = classNames(
      getTextColorClassName(textColor),
      getBackgroundColorClassName(backgroundColor),
      dangerous && getColorSchemeClassName('dangerous'),
      disabled && getColorSchemeClassName('disabled') ||
      positive && getColorSchemeClassName('positive') ||
      colorScheme && getColorSchemeClassName(colorScheme),
      (withShadow && !disabled) && 'with--shadow',
      variant !== 'icon' && variant !== 'icon-no-padding' && !!icon && `with--icon`,
      !!icon && !!label && `icon-position--${iconPosition}`,
      className,
    );

    return (
      <WrapperComponent
        title={title}
        {...classes('', classesModifiers, extraClasses) }
        {...wrapperProps}
        {...routerLinkProps}
        {...hyperLinkProps}
      >
        <span {...classes('flex') }>
          <div {...classes('active-background') } />
          <div {...classes('wrapper') }>
            {iconPosition === 'left' && Icon}
            {label ? typeof label === 'string' ?
              <Span {...classes('label') }>{label}</Span>
              : React.cloneElement(label as React.ReactElement<any>, { ...classes('label', '', 'label') })
              : undefined
            }
            {children}
            {iconPosition === 'right' && Icon}
          </div>
        </span>
      </WrapperComponent>
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/atoms/buttons/Button/index.tsx